import { computed, action } from 'mobx';

import { Model } from 'core/model';
import api from 'core/util/api';
import { timezone, formatDateTime, humanizeDuration } from 'core/util/dateUtils';
import { showSuccessToast } from 'core/components/toast';
import { SUPPRESSION_TYPES } from '@kentik/ui-shared/alerting/constants';
import { checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import { getAlertSuppressionStartTime } from '@kentik/ui-shared/alertingManager/utils';
import safelyParseJSON from '@kentik/ui-shared/util/safelyParseJson';
import {
  getFilterOperator,
  CONDITION_OPERATOR_MAP,
  BOOLEAN_CONDITION_OPERATORS,
  buildKeyValueFilters
} from 'app/forms/config/suppressions/suppression';

export default class SuppressionModel extends Model {
  get urlRoot() {
    return '/api/ui/alerting/suppressions';
  }

  serialize(data = {}) {
    const {
      application,
      ruleId,
      rule_id,
      keyFilter,
      dimensionToKeyPart,
      startTime,
      expirationTime,
      endTime,
      agentIds, // used for synth
      testIds, // used for synth
      strict,
      comment,
      applicationMetadata = {}
    } = data;

    const resolvedKeyFilter =
      keyFilter ||
      (dimensionToKeyPart
        ? {
            filters: buildKeyValueFilters(
              Object.entries(dimensionToKeyPart)
                .filter(([, value]) => value !== '' && value !== undefined && value !== '---')
                .map(([filterField, filterValue]) => ({ filterField, filterOperator: 'equals', filterValue }))
            )
          }
        : undefined);

    const suppression = {
      application,
      ruleId: ruleId || rule_id,
      keyFilter: resolvedKeyFilter,
      comment,
      type: this.type,
      strict,
      agentIds,
      testIds,
      startTime: getAlertSuppressionStartTime(startTime),
      expirationTime: endTime || expirationTime,
      applicationMetadata:
        typeof applicationMetadata === 'object' ? JSON.stringify(applicationMetadata, null, 2) : applicationMetadata
    };

    return super.serialize({ suppression });
  }

  deserialize(data = {}) {
    const serializedSuppression = data?.suppression || data;
    const { user_id, rule_id, start_time, end_time, application_metadata, ...restSuppression } = serializedSuppression;

    const suppression = {
      ...restSuppression,
      userId: user_id,
      ruleId: rule_id,
      startTime: start_time,
      expirationTime: end_time,
      applicationMetadata: safelyParseJSON(application_metadata)
    };

    return super.deserialize(suppression);
  }

  getDestroyUrl = (url, suppressionID) =>
    url ?? (this?.collection?.urlPaths?.destroy ? `${this.collection.urlPaths.destroy}/${suppressionID}` : this.url);

  handleDestroySuccess = (options) => {
    const { toast = true, remove = true } = options;
    return action((success) => {
      if (toast) {
        showSuccessToast(this.messages.destroy, {
          title: this.showToastTitles ? 'Removed' : undefined
        });
      }

      if (this.collection) {
        if (remove) {
          this.collection.remove([this.id]);
        }

        this.collection.clearSelection();
      }

      this.requestStatus = null;

      return success;
    });
  };

  handleDestroyFail = (options) =>
    action((error) => {
      console.error('destroyModel error', error);
      if (options.optimistic && this.collection) {
        this.collection.add([this.get()]);
      }
      this.error = { label: 'destroying', body: error };
      this.requestStatus = null;

      return error;
    });

  getSortValue(field) {
    if (field === 'expirationDetails') {
      return this.get('expirationTime');
    }

    return super.getSortValue(field);
  }

  @action
  destroyModel = async (options = {}) => {
    const { url } = options;
    this.requestStatus = 'destroying';

    return api
      .del(this.getDestroyUrl(url, this.get('id')), { data: this.get() })
      .then(this.handleDestroySuccess(options), this.handleDestroyFail(options));
  };

  @computed
  get expired() {
    const expirationTime = this.get('expirationTime');
    return !expirationTime ? false : timezone.momentFn(expirationTime).isBefore();
  }

  @computed
  get expirationDetails() {
    const expirationTime = this.get('expirationTime');
    if (!expirationTime) {
      return { expirationDay: null, expired: false, duration: null };
    }

    return {
      expirationDay: formatDateTime(expirationTime),
      duration: humanizeDuration(timezone.momentFn(expirationTime).diff())
    };
  }

  @computed
  get startTime() {
    const startTime = this.get('startTime');
    return formatDateTime(startTime);
  }

  @computed
  get startDetails() {
    const startTimeMoment = timezone.momentFn(this.get('startTime'));
    return {
      started: startTimeMoment.isSameOrBefore(),
      futureStart: startTimeMoment.isAfter(),
      diff: humanizeDuration(startTimeMoment.diff())
    };
  }

  get userId() {
    return this.get('userId');
  }

  get ruleId() {
    return this.get('ruleId');
  }

  @computed
  get createdDay() {
    return formatDateTime(this.get('cdate'));
  }

  get comment() {
    return this.get('comment');
  }

  get messages() {
    return {
      create: `Suppression created${this.comment ? ` '${this.comment}'` : ''}. It will take effect in a few minutes.`,
      update: `Suppression updated ${this.comment ? `'${this.comment}'` : ''}`,
      destroy: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} removed successfully`,
      duplicate: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} duplicated successfully`
    };
  }

  get agentIds() {
    // assemble a unique list of all agent ids --- those from creation and others picked up in filters during editing
    const filters = this.get('key_filter.filters') || [];
    const agentIdFilterValues = filters.find(
      // allow to find agent ids in the suppression filter two ways
      // the first would be in the initial converted style which is broken into two single filters
      // the second is the newer form; a single, multi-value filter
      // the reason we check both is because the second form that uses a 'values' match on key is not able to translate into the single filters
      (filter) => filter.key?.equals === 'agentId' || (filter.key?.in?.values || []).includes('agentId')
    )?.value?.in?.values;
    const allAgentIds = [].concat(this.get('agentIds') || [], agentIdFilterValues || []);
    const uniqueAgentIds = Array.from(new Set(allAgentIds));

    return uniqueAgentIds.filter((agentId) => !!this.store.$syn.agents.get(agentId));
  }

  get testIds() {
    // assemble a unique list of all test ids --- those from creation and others picked up in filters during editing
    const filters = this.get('key_filter.filters') || [];
    const testIdFilterValues = filters.find((filter) => filter.key?.equals === 'testId')?.value?.in?.values;
    const allTestIds = [].concat(this.get('testIds') || [], testIdFilterValues || []);
    const uniqueTestIds = Array.from(new Set(allTestIds));

    return uniqueTestIds.filter((testId) => !!this.store.$syn.tests.get(testId));
  }

  get applicationMetadata() {
    return this.get('applicationMetadata', {});
  }

  get measurement() {
    return this.applicationMetadata.measurement;
  }

  get type() {
    const { type, application } = this.applicationMetadata;
    const filters = this.get('key_filter.filters') || [];
    const hasFilters = filters.length;
    let suppressionType = type;

    if (this.agentIds.length > 0) {
      suppressionType = SUPPRESSION_TYPES.AGENT;
    } else if (this.testIds.length > 0) {
      suppressionType = SUPPRESSION_TYPES.SYNTH;
    } else {
      if (!hasFilters && this.ruleId) {
        suppressionType = SUPPRESSION_TYPES.POLICY;
      }

      if (hasFilters && checkApplication.isMetric(application)) {
        suppressionType = SUPPRESSION_TYPES.METRIC;
      }

      if (hasFilters && checkApplication.isNms(application)) {
        suppressionType = SUPPRESSION_TYPES.NMS;
      }

      // These are bad legacy types that should be 'condition'
      if (type === 'partial' || type === 'full') {
        suppressionType = SUPPRESSION_TYPES.CONDITION;
      }
    }

    return suppressionType || SUPPRESSION_TYPES.CONDITION;
  }

  get isEditable() {
    return !this.get('isLegacy');
  }

  get isSuppression() {
    return true;
  }

  get policyDisplayName() {
    const { $alerting } = this.store;
    const rulePolicyMatch = $alerting.policyCollection.getPolicyByRuleId(this.get('ruleId'));
    if (rulePolicyMatch) {
      return `${rulePolicyMatch.name} - (${rulePolicyMatch.id})`;
    }

    const policyId = this.applicationMetadata?.policyId || this.get('policyID');
    const policy = $alerting.policyCollection.models.find(
      (collectionPolicy) => `${collectionPolicy.id}` === `${policyId}`
    );

    if (policy) {
      const policyName = policy.name || policy.get('name');

      if (policyName) {
        return `${policyName} - (${policyId})`;
      }

      return policyId;
    }

    return 'Missing Policy';
  }

  @computed
  get dimensions() {
    const filters = this.get('key_filter.filters', []);

    return filters.reduce((allFilters, { key, value }) => {
      const dimensionName = key?.equals;
      const dimensionValue = value?.equals || value?.in?.values;

      return {
        ...allFilters,
        [dimensionName]: dimensionValue
      };
    }, {});
  }

  get rawFilters() {
    const filters = this.get('key_filter.filters', []);

    return filters.reduce((acc, { key, value }) => {
      const fieldName = key?.equals;
      if (!fieldName) {
        return acc;
      }

      const filterOperator = getFilterOperator(value);
      const isBooleanOperator = BOOLEAN_CONDITION_OPERATORS.includes(CONDITION_OPERATOR_MAP[filterOperator].key);

      return [
        ...acc,
        {
          filterField: fieldName,
          filterOperator,
          filterValue: isBooleanOperator ? null : value[CONDITION_OPERATOR_MAP[filterOperator].key]
        }
      ];
    }, []);
  }
}
